import { useWallet } from '@solana/wallet-adapter-react'
import { Connection, PublicKey } from '@solana/web3.js'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { notify } from 'utils/notifications'
import {
  getTokenAccountsByOwnerWithWrappedSol,
  getToken2022AccountsByOwner,
  TokenAccount,
} from 'utils/tokens'
import { useCallback } from 'react'
import { milliseconds } from 'date-fns'
import { TRITON_URL_BACKUP } from 'utils/constants'
import { useTranslation } from 'next-i18next'
import { toContextTuplesString } from 'utils/logging'

const fetchWalletTokens = async (
  walletPk: PublicKey | null,
  translations: { fetchFailed: string },
): Promise<TokenAccount[]> => {
  //we use backup url to split the traffic between dedicated and shared nodes
  const connection = new Connection(TRITON_URL_BACKUP, 'confirmed')
  if (!walletPk) return []

  try {
    const [tokenAccountsWithWsol, token2022Accounts] = await Promise.all(
      [
        {
          promise: getTokenAccountsByOwnerWithWrappedSol(connection, walletPk),
          forProgram: 'token',
        },
        {
          promise: getToken2022AccountsByOwner(connection, walletPk),
          forProgram: 'token2022',
        },
      ].map(async ({ promise, forProgram }) => {
        try {
          return await promise
        } catch (e) {
          console.error(
            `Failed to fetch wallet tokens: ${toContextTuplesString({
              walletPk: walletPk.toString(),
              forProgram,
            })}`,
            e,
          )
          throw e
        }
      }),
    )

    return [...tokenAccountsWithWsol, ...token2022Accounts].filter(
      (token) => token.uiAmount > 0,
    )
  } catch (e) {
    notify({
      title: translations.fetchFailed,
      type: 'info',
    })
    return []
  }
}

const makeWalletTokensQueryKey = (publicKey: PublicKey | null) => [
  'wallet-tokens',
  publicKey?.toString(),
]

export const useWalletTokens = () => {
  const { t } = useTranslation('wallet')
  const { publicKey } = useWallet()
  const {
    data: walletTokens,
    isInitialLoading: loading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: makeWalletTokensQueryKey(publicKey),
    queryFn: () =>
      fetchWalletTokens(publicKey, {
        fetchFailed: t('errors.failed-to-refresh'),
      }),
    retry: 3,
    cacheTime: milliseconds({ seconds: 10 }),
    staleTime: milliseconds({ seconds: 10 }),
    refetchOnWindowFocus: false,
    enabled: !!publicKey,
  })

  return {
    walletTokens,
    loading,
    refetch,
    isFetching,
  }
}

export function useInvalidateWalletTokens() {
  const queryClient = useQueryClient()
  const { publicKey } = useWallet()

  return useCallback(async () => {
    if (!publicKey) return

    return queryClient.invalidateQueries({
      queryKey: makeWalletTokensQueryKey(publicKey),
    })
  }, [queryClient, publicKey])
}
